import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Brain, ChevronsUpDown, Plus, Trash2, TriangleAlert, Workflow, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { EvalReportView } from "@/components/eval-report";
import { PopeIcon } from "@/components/pope-icon";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { ErrorState, Loading } from "@/components/query-state";
import type { EvalReport, PolicyContent, PolicyCoverage, PublishResult, SourceKind, ToolCatalog, ToolCatalogAction, ToolCatalogTool } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { findCapabilityRefs } from "@/lib/capability-refs";
import { capabilityGroups, groupKind } from "@/lib/capability-catalog";
import { addTagInput, MAX_TAG_KEYS, MAX_TAG_VALUES, removeTag, type PolicyTags } from "@/lib/policy-tags";

const SOURCE_KINDS: SourceKind[] = ["slack", "ticket", "doc", "contrato", "outro"];

const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const text = (a: string[]) => a.join("\n");

interface FormState {
  policy_id: string; domain: string; title: string; situation: string; rule: string; customer_text: string;
  agent_steps: string; escalate_when: string; never_say: string;
  available: { ref: string; when: string }[];
  effective_from: string; effective_until: string;
  overrides: string;
  sources: { kind: SourceKind; ref: string }[];
  should_match: string; should_not_match: string;
  change_reason: string;
  tags: Record<string, string[]>;
}

const emptyForm = (): FormState => ({
  policy_id: "", domain: "", title: "", situation: "", rule: "", customer_text: "",
  agent_steps: "", escalate_when: "", never_say: "", available: [],
  effective_from: "", effective_until: "", overrides: "", sources: [],
  should_match: "", should_not_match: "", change_reason: "", tags: {},
});

const toForm = (c: PolicyContent): FormState => ({
  policy_id: c.policy_id, domain: c.domain, title: c.title, situation: c.situation, rule: c.rule, customer_text: c.customer_text,
  agent_steps: text(c.agent_steps), escalate_when: text(c.escalate_when), never_say: text(c.never_say), available: c.available ?? [],
  effective_from: c.effective_from ?? "", effective_until: c.effective_until ?? "", overrides: text(c.overrides), sources: c.sources,
  should_match: text(c.eval_cases.should_match), should_not_match: text(c.eval_cases.should_not_match), change_reason: c.change_reason,
  tags: c.tags ?? {},
});

/** Keys without values impose no restriction, so they leave the content (RFC-0005 T3). */
const restrictedTags = (tags: Record<string, string[]>) =>
  Object.fromEntries(Object.entries(tags).filter(([, values]) => values.length > 0));

const toContent = (f: FormState): PolicyContent => ({
  policy_id: f.policy_id.trim(), domain: f.domain, title: f.title.trim(), situation: f.situation.trim(), rule: f.rule.trim(),
  customer_text: f.customer_text.trim(), agent_steps: lines(f.agent_steps), escalate_when: lines(f.escalate_when),
  never_say: lines(f.never_say), available: f.available,
  effective_from: f.effective_from || null, effective_until: f.effective_until || null,
  overrides: lines(f.overrides), sources: f.sources,
  eval_cases: { should_match: lines(f.should_match), should_not_match: lines(f.should_not_match) },
  change_reason: f.change_reason.trim(),
  ...(Object.keys(restrictedTags(f.tags)).length > 0 ? { tags: restrictedTags(f.tags) } : {}),
});

function reportFromError(error: unknown): EvalReport | null {
  if (error instanceof ApiError && typeof error.body === "object" && error.body && "report" in error.body) {
    return ((error.body as { report: EvalReport | null }).report ?? null);
  }
  return null;
}

type ValidationIssue = { path: string | Array<string | number>; message?: string };

function validationIssues(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || error.status !== 400 || typeof error.body !== "object" || !error.body || !("issues" in error.body)) return {};
  const issues = (error.body as { issues?: ValidationIssue[] }).issues;
  if (!Array.isArray(issues)) return {};
  const result: Record<string, string> = {};
  for (const issue of issues) {
    if (!issue) continue;
    const parts = (Array.isArray(issue.path) ? issue.path : String(issue.path).replace(/\[(\d+)\]/g, ".$1").split("."))
      .map(String).filter(Boolean);
    if (parts[0] === "content" || parts[0] === "policy") parts.shift();
    const path = parts.join(".");
    const field = path.startsWith("eval_cases.") ? path.slice("eval_cases.".length)
      : path.startsWith("available.") ? "available"
      : path.startsWith("sources.") ? "sources" : path;
    if (field && !result[field]) result[field] = issue.message ?? error.message;
  }
  return result;
}

function CharacterCounter({ value, max, items = false }: { value: string; max: number; items?: boolean }) {
  const { t } = useTranslation();
  const count = items ? lines(value).length : value.length;
  return <p className="text-muted text-right text-xs">{t(items ? "editor.itemCount" : "editor.characterCount", { count, max })}</p>;
}

function ItemCounter({ count, max }: { count: number; max: number }) {
  const { t } = useTranslation();
  return <p className="text-muted text-right text-xs">{t("editor.itemCount", { count, max })}</p>;
}

function FieldIssue({ message }: { message?: string }) {
  return message ? <p role="alert" className="text-danger text-xs">{message}</p> : null;
}

type CatalogActionOption = { tool: ToolCatalogTool; action: ToolCatalogAction };

function GroupIcon({ tool, className }: { tool: ToolCatalogTool; className: string }) {
  const kind = groupKind(tool);
  if (kind === "workflow") return <Workflow size={14} aria-hidden="true" className={className} />;
  if (kind === "memory") return <Brain size={14} aria-hidden="true" className={className} />;
  return null;
}

function GroupBadge({ tool, className }: { tool: ToolCatalogTool; className: string }) {
  const { t } = useTranslation();
  const kind = groupKind(tool);
  if (kind === null) return null;
  return <span className={className}>{t(kind === "workflow" ? "editor.workflowLabel" : "editor.memoryLabel")}</span>;
}

function ToolPicker({
  id,
  label,
  value,
  catalog,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  catalog?: ToolCatalog;
  disabled: boolean;
  onChange: (ref: string) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const blurTimeout = useRef<number | undefined>(undefined);
  const catalogTools = capabilityGroups(catalog, { workflows: t("editor.workflowsHint"), memory: t("editor.memoryHint") });
  const groupTitle = (tool: ToolCatalogTool) => {
    const kind = groupKind(tool);
    return kind === "workflow" ? t("editor.workflows") : kind === "memory" ? t("editor.memory") : tool.name;
  };
  const selected = catalogTools.flatMap((tool) =>
    tool.actions.map((action) => ({ tool, action })),
  ).find(({ action }) => action.ref === value);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const groups = catalogTools.map((tool) => ({
    tool,
    actions: tool.actions.filter((action) =>
      `${tool.name} ${tool.description} ${action.name} ${action.ref} ${action.description}`
        .toLocaleLowerCase()
        .includes(normalizedQuery),
    ),
  })).filter((group) => group.actions.length > 0);
  const options: CatalogActionOption[] = groups.flatMap(({ tool, actions }) =>
    actions.map((action) => ({ tool, action })),
  );
  const listId = `${id}-options`;
  const optionId = (option: CatalogActionOption) =>
    `${id}-option-${option.tool.name}-${option.action.name}`;
  const activeId = options[activeIndex] ? optionId(options[activeIndex]!) : undefined;

  const choose = ({ action }: CatalogActionOption) => {
    onChange(action.ref);
    setQuery("");
    setActiveIndex(0);
    setOpen(false);
  };

  const close = () => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(options.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && open && options[activeIndex]) {
      event.preventDefault();
      choose(options[activeIndex]!);
    } else if (event.key === "Escape") {
      close();
    }
  };

  useEffect(() => () => {
    if (blurTimeout.current !== undefined) window.clearTimeout(blurTimeout.current);
  }, []);

  // The selection sits inside the same field-height control as the search input. It shows while
  // the search is closed, and the input takes its place once the author clicks or types.
  const showSelection = Boolean(value) && !open && query === "";
  const selectionId = `${id}-selection`;
  const unknown = Boolean(catalog && value && !selected);

  return (
    <div className="flex flex-col gap-2">
      <div data-picker-control className="relative">
        <Input
          id={id}
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open ? activeId : undefined}
          aria-describedby={showSelection ? selectionId : undefined}
          aria-invalid={unknown}
          className="cursor-pointer pr-9 focus:cursor-text"
          placeholder={showSelection ? undefined : t("editor.toolSearch")}
          value={query}
          disabled={disabled}
          onFocus={() => {
            if (blurTimeout.current !== undefined) window.clearTimeout(blurTimeout.current);
            blurTimeout.current = undefined;
          }}
          onClick={() => setOpen(true)}
          onBlur={() => {
            blurTimeout.current = window.setTimeout(() => {
              blurTimeout.current = undefined;
              close();
            }, 100);
          }}
          onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
        {showSelection ? (
          <div id={selectionId} className={`pointer-events-none absolute inset-y-0 right-9 left-3 flex min-w-0 items-center gap-2 text-sm ${disabled ? "opacity-60" : ""}`}>
            {selected ? (
              <>
                <GroupIcon tool={selected.tool} className="text-accent shrink-0" />
                <span className="truncate font-medium">{selected.tool.name}.{selected.action.name}</span>
                <GroupBadge tool={selected.tool} className="bg-accent-soft shrink-0 rounded px-1.5 py-0.5 text-xs" />
                <span className="bg-surface-raised text-secondary shrink-0 rounded px-1.5 py-0.5 text-xs">{t(selected.action.kind === "read" ? "editor.toolRead" : "editor.toolWrite")}</span>
              </>
            ) : unknown ? (
              <span role="alert" className="text-danger truncate text-xs">{t("editor.unknownCapability", { ref: value })}</span>
            ) : (
              <span className="text-muted truncate">{value}</span>
            )}
          </div>
        ) : null}
        <ChevronsUpDown size={14} aria-hidden="true" className="text-muted pointer-events-none absolute top-1/2 right-3 -translate-y-1/2" />
      </div>
      {open && options.length > 0 ? (
        <ul id={listId} role="listbox" aria-label={label} className="border-border-subtle bg-surface max-h-64 overflow-auto rounded-md border">
          {groups.map(({ tool, actions }) => (
            <li key={tool.name} role="group" aria-label={groupTitle(tool)}>
              <p className="text-muted px-3 pt-2 text-xs font-medium">{groupTitle(tool)}</p>
              <p className="text-muted px-3 text-xs">{tool.description}</p>
              {actions.map((action) => {
                const option = { tool, action };
                const index = options.findIndex((item) => item.action.ref === action.ref);
                return (
                  <div
                    id={optionId(option)}
                    key={action.ref}
                    role="option"
                    aria-selected={value === action.ref}
                    className={`cursor-pointer px-3 py-2 text-sm ${index === activeIndex ? "bg-surface-raised" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(option)}
                  >
                    <GroupIcon tool={tool} className="text-accent mr-1.5 inline align-[-2px]" />
                    <span className="font-medium">{action.name}</span>
                    <span className="text-muted ml-2 text-xs">{action.description}</span>
                    <GroupBadge tool={tool} className="bg-accent-soft ml-2 rounded px-1.5 py-0.5 text-xs" />
                    <span className="bg-surface-raised text-secondary ml-2 rounded px-1.5 py-0.5 text-xs">{t(action.kind === "read" ? "editor.toolRead" : "editor.toolWrite")}</span>
                  </div>
                );
              })}
            </li>
          ))}
        </ul>
      ) : null}
      {open && options.length === 0 ? <p role="status" className="text-muted text-sm">{t("editor.toolSearchEmpty")}</p> : null}
    </div>
  );
}

type CapabilitySuggestion = { tool: ToolCatalogTool; action: ToolCatalogAction };
type CapabilityQuery = { start: number; end: number; query: string };

function capabilityQueryAt(value: string, cursor: number): CapabilityQuery | null {
  const beforeCursor = value.slice(0, cursor);
  const start = beforeCursor.lastIndexOf("@");
  if (start < 0 || (start > 0 && /[a-z0-9_]/i.test(beforeCursor[start - 1]!))) return null;
  const query = beforeCursor.slice(start + 1);
  if (!/^[a-z0-9_]*(?:\.[a-z0-9_]*)?$/i.test(query)) return null;
  const suffixLength = value.slice(cursor).match(/^[a-z0-9_.]*/i)?.[0].length ?? 0;
  return { start, end: cursor + suffixLength, query };
}

function renderStepHighlights(value: string) {
  const parts = [];
  let offset = 0;
  for (const { start, end } of findCapabilityRefs(value)) {
    if (start > offset) parts.push(value.slice(offset, start));
    parts.push(<mark key={start} className="bg-accent-soft text-foreground rounded-sm">{value.slice(start, end)}</mark>);
    offset = end;
  }
  if (offset < value.length) parts.push(value.slice(offset));
  return parts;
}

function AgentStepsEditor({
  value,
  catalog,
  disabled,
  invalid,
  onChange,
}: {
  value: string;
  catalog?: ToolCatalog;
  disabled: boolean;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const pendingCursor = useRef<number | null>(null);
  const suppressNextSelect = useRef(false);
  const [query, setQuery] = useState<CapabilityQuery | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const actions: CapabilitySuggestion[] = capabilityGroups(catalog, { workflows: t("editor.workflowsHint"), memory: t("editor.memoryHint") }).flatMap((tool) =>
    tool.actions.map((action) => ({ tool, action })),
  );
  const normalizedQuery = query?.query.toLocaleLowerCase() ?? "";
  const suggestions = actions.filter(({ tool, action }) =>
    `${action.ref} ${tool.name} ${tool.description} ${action.name} ${action.description}`
      .toLocaleLowerCase()
      .includes(normalizedQuery),
  );
  const listId = "agent-steps-capability-options";
  const optionId = (suggestion: CapabilitySuggestion) => `agent-steps-capability-${suggestion.action.ref}`;
  const activeId = suggestions[activeIndex] ? optionId(suggestions[activeIndex]!) : undefined;

  useLayoutEffect(() => {
    if (pendingCursor.current === null || !textareaRef.current) return;
    const cursor = pendingCursor.current;
    pendingCursor.current = null;
    suppressNextSelect.current = true;
    textareaRef.current.setSelectionRange(cursor, cursor);
    window.setTimeout(() => { suppressNextSelect.current = false; }, 0);
  }, [value]);

  const updateQuery = (nextValue: string, cursor: number, selectionEnd = cursor) => {
    if (cursor !== selectionEnd) {
      setQuery(null);
      return;
    }
    const nextQuery = capabilityQueryAt(nextValue, cursor);
    const unchanged = query?.start === nextQuery?.start && query?.end === nextQuery?.end && query?.query === nextQuery?.query;
    if (!unchanged) setActiveIndex(0);
    setQuery(nextQuery);
  };

  const choose = (suggestion: CapabilitySuggestion) => {
    const range = query;
    if (!range) return;
    const inserted = `@${suggestion.action.ref}`;
    const nextValue = `${value.slice(0, range.start)}${inserted}${value.slice(range.end)}`;
    pendingCursor.current = range.start + inserted.length;
    onChange(nextValue);
    setQuery(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!query) return;
    if (event.key === "Escape") {
      event.preventDefault();
      setQuery(null);
      return;
    }
    if (suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(suggestions[activeIndex]!);
    }
  };

  return (
    <div className="relative">
      <div
        ref={mirrorRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-md border border-transparent px-[13px] py-[9px] text-sm leading-5 text-foreground"
      >
        {renderStepHighlights(value)}
      </div>
      <Textarea
        ref={textareaRef}
        id="agent_steps"
        role="combobox"
        aria-label={t("editor.agentSteps")}
        aria-autocomplete="list"
        aria-expanded={Boolean(query && suggestions.length)}
        aria-controls={listId}
        aria-activedescendant={query ? activeId : undefined}
        aria-invalid={invalid}
        value={value}
        disabled={disabled}
        className="relative resize-y bg-transparent text-transparent caret-foreground selection:bg-accent-soft selection:text-transparent leading-5"
        style={{ caretColor: "var(--foreground)" }}
        onChange={(event) => {
          onChange(event.target.value);
          updateQuery(event.target.value, event.target.selectionStart, event.target.selectionEnd);
        }}
        onKeyDown={onKeyDown}
        onKeyUp={(event) => {
          if (event.key === "Escape" || (suggestions.length > 0 && ["ArrowDown", "ArrowUp", "Enter"].includes(event.key))) return;
          updateQuery(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget.selectionEnd);
        }}
        onClick={(event) => updateQuery(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget.selectionEnd)}
        onSelect={(event) => {
          if (suppressNextSelect.current) {
            suppressNextSelect.current = false;
            return;
          }
          updateQuery(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget.selectionEnd);
        }}
        onScroll={(event) => {
          if (mirrorRef.current) {
            mirrorRef.current.scrollTop = event.currentTarget.scrollTop;
            mirrorRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
        }}
        onBlur={() => {
          setQuery(null);
        }}
      />
      {query && suggestions.length > 0 ? (
        <ul id={listId} role="listbox" aria-label={t("editor.capabilitySuggestions")} className="border-border-subtle bg-surface absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded-md border shadow-md">
          {suggestions.map((suggestion, index) => (
            <li
              id={optionId(suggestion)}
              key={suggestion.action.ref}
              role="option"
              aria-selected={index === activeIndex}
              className={`cursor-pointer px-3 py-2 text-sm ${index === activeIndex ? "bg-surface-raised" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(suggestion)}
            >
              <GroupIcon tool={suggestion.tool} className="text-accent mr-1.5 inline align-[-2px]" />
              <span className="font-medium">@{suggestion.action.ref}</span>
              <span className="text-muted ml-2 text-xs">{suggestion.action.description}</span>
              <GroupBadge tool={suggestion.tool} className="bg-accent-soft ml-2 rounded px-1.5 py-0.5 text-xs" />
            </li>
          ))}
        </ul>
      ) : null}
      {query && suggestions.length === 0 ? <p role="status" className="text-muted mt-2 text-sm">{t("editor.capabilitySearchEmpty")}</p> : null}
    </div>
  );
}

/** D11: a warning only. It never disables save or publish. */
function CoverageWarning({ coverage }: { coverage?: PolicyCoverage }) {
  const { t } = useTranslation();
  if (!coverage) return null;
  if (!coverage.available) {
    return (
      <section aria-label={t("editor.coverageTitle")} className="bg-surface-sunken text-secondary rounded-md px-3 py-2 text-sm">
        {t("editor.coverageUnavailable")}
      </section>
    );
  }
  const missing = coverage.minions.filter((minion) => minion.missingRefs.length > 0);
  if (missing.length === 0) return null;
  return (
    <section aria-label={t("editor.coverageTitle")} className="bg-warning-soft text-secondary flex gap-2 rounded-md px-3 py-2 text-sm">
      <TriangleAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-foreground font-medium">{t("editor.coverageMissing")}</p>
        <ul className="flex flex-col gap-0.5">
          {missing.map((minion) => (
            <li key={minion.minionId} className="break-words">
              <span className="text-foreground font-medium">{minion.minionId}</span>
              {": "}
              {minion.missingRefs.map((ref) => `@${ref}`).join(", ")}
              {minion.help ? null : <span className="text-muted"> ({t("editor.coverageNoHelp")})</span>}
            </li>
          ))}
        </ul>
        <p className="text-muted text-xs">{t("editor.coverageHint")}</p>
      </div>
    </section>
  );
}

function AudienceTags({
  tags,
  disabled,
  issueFor,
  onChange,
}: {
  tags: PolicyTags;
  disabled: boolean;
  issueFor: (key: string) => string | undefined;
  onChange: (tags: PolicyTags) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const keys = Object.keys(tags).filter((key) => tags[key].length > 0);

  const add = (raw: string) => {
    const result = addTagInput(tags, raw);
    onChange(result.tags);
    const rest = [...result.invalid, ...result.overLimit];
    setDraft(rest.join(" "));
    setProblem(
      result.invalid.length > 0
        ? t("editor.tagsInvalid", { tokens: result.invalid.join(", ") })
        : result.overLimit.length > 0
          ? t("editor.tagsLimit", { maxKeys: MAX_TAG_KEYS, maxValues: MAX_TAG_VALUES })
          : null,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-muted text-sm">{t("editor.tagsSemantics")}</p>
        <p className="text-muted text-sm">{t("editor.tagsFiltersNote")}</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="tags-input" className="text-secondary text-sm font-medium">{t("editor.tagsInput")}</label>
        <div className="flex min-w-0 gap-2">
          <Input
            id="tags-input"
            className="min-w-0 flex-1 font-mono"
            placeholder="regiao:sp, tipo-batata:frita"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            disabled={disabled}
            value={draft}
            aria-invalid={problem ? true : undefined}
            aria-describedby="tags-input-hint"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add(draft);
              }
            }}
            onPaste={(event) => {
              const pasted = event.clipboardData.getData("text");
              if (!/[\s,]/.test(pasted.trim())) return;
              event.preventDefault();
              add(`${draft} ${pasted}`);
            }}
          />
          <Button variant="secondary" className="shrink-0" disabled={disabled || draft.trim() === ""} onClick={() => add(draft)}>
            <Plus size={14} aria-hidden /> {t("editor.tagsAdd")}
          </Button>
        </div>
        <p id="tags-input-hint" className="text-muted text-xs">{t("editor.tagsHint")}</p>
        <FieldIssue message={problem ?? undefined} />
      </div>
      {keys.length === 0 ? (
        <p className="text-muted text-sm">{t("editor.tagsEmpty")}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {keys.map((key) => {
            const labelId = `tags-${key}-label`;
            return (
              <li key={key} role="group" aria-labelledby={labelId} className="border-border-subtle flex min-w-0 flex-col gap-2 rounded-md border px-3 py-2.5 sm:flex-row sm:items-start sm:gap-3">
                <span id={labelId} className="text-secondary font-mono text-sm font-medium break-all sm:w-40 sm:shrink-0 sm:py-1.5 sm:leading-5">{key}</span>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <ul className="flex flex-wrap gap-1.5">
                    {tags[key].map((value) => (
                      <li key={value} className="border-accent bg-accent-soft text-foreground inline-flex min-h-8 max-w-full items-center gap-1 rounded-md border py-0.5 pr-0.5 pl-2.5 font-mono text-sm">
                        <span className="break-all">{value}</span>
                        <button
                          type="button"
                          className="text-muted hover:text-foreground inline-flex size-7 shrink-0 items-center justify-center rounded-md disabled:opacity-60"
                          aria-label={t("editor.tagsRemove", { tag: `${key}:${value}` })}
                          disabled={disabled}
                          onClick={() => onChange(removeTag(tags, key, value))}
                        >
                          <X size={14} aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <FieldIssue message={issueFor(key)} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const publishOutcomeKey = (result: PublishResult) => `outcomes.${result.outcome}${result.liveVersion === null ? "" : "WithLive"}`;

export function PolicyEditor() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isNew = !id;
  const location = useLocation();
  const routeFeedback = location.state as { publishMessage?: string; report?: EvalReport | null; reportUnavailable?: boolean } | null;
  const navigate = useNavigate();
  const client = useQueryClient();

  const domains = useQuery({ queryKey: ["domains"], queryFn: api.listDomains });
  const toolCatalog = useQuery({ queryKey: ["tools"], queryFn: api.tools });
  const existing = useQuery({ queryKey: ["policy", id], queryFn: () => api.getPolicy(id!), enabled: !isNew });
  const coverage = useQuery({ queryKey: ["coverage", id], queryFn: () => api.coverage(id!), enabled: !isNew });

  const [form, setForm] = useState<FormState>(emptyForm);
  const [report, setReport] = useState<EvalReport | null>(() => routeFeedback?.report ?? null);
  const [reportUnavailable, setReportUnavailable] = useState(() => routeFeedback?.reportUnavailable ?? false);
  const [publishMessage, setPublishMessage] = useState<string | null>(() => routeFeedback?.publishMessage ?? null);
  const dirty = useRef(false);
  const seededKey = useRef<string | null>(null);
  useEffect(() => {
    const data = existing.data;
    const draft = data?.draft ?? data?.live?.content;
    if (!data || !draft) return;
    const key = `${data.policyId}:${data.liveVersion}:${JSON.stringify(draft)}`;
    const samePolicy = seededKey.current?.startsWith(`${data.policyId}:`);
    if (key === seededKey.current || (samePolicy && dirty.current)) return;
    seededKey.current = key;
    dirty.current = false;
    setForm(toForm(draft));
  }, [existing.data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    dirty.current = true;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async (): Promise<string> => {
    const content = toContent(form);
    if (isNew) {
      const created = await api.createPolicy(content);
      return created.policyId ?? content.policy_id;
    }
    await api.saveDraft(id!, content);
    return id!;
  };

  const saveDraft = useMutation({
    mutationFn: save,
    onSuccess: (pid) => {
      void client.invalidateQueries({ queryKey: ["policy", pid] });
      void client.invalidateQueries({ queryKey: ["coverage", pid] });
      if (isNew) navigate(`/policies/${pid}/edit`);
    },
  });

  const publish = useMutation({
    mutationFn: async () => {
      const pid = await save();
      const result = await api.publish(pid);
      return { pid, result };
    },
    onMutate: () => { setReport(null); setReportUnavailable(false); setPublishMessage(null); },
    onSuccess: ({ pid, result }) => {
      void client.invalidateQueries({ queryKey: ["policy", pid] });
      void client.invalidateQueries({ queryKey: ["coverage", pid] });
      setReport(result.report ?? null);
      setReportUnavailable(!result.report);
      const message = t(publishOutcomeKey(result), {
        candidate: `v${result.candidateVersion}`,
        live: result.liveVersion === null ? "" : `v${result.liveVersion}`,
      });
      setPublishMessage(message);
      if (result.outcome === "activated") navigate(`/policies/${pid}`, { state: { publishMessage: message, report: result.report } });
      else if (isNew) navigate(`/policies/${pid}/edit`, { state: { publishMessage: message, report: result.report, reportUnavailable: !result.report } });
    },
    onError: (error) => {
      setReport(reportFromError(error));
      setReportUnavailable(!reportFromError(error) && (!(error instanceof ApiError) || error.status >= 500));
    },
  });

  if (!isNew && existing.isPending) return <Loading />;
  if (!isNew && existing.isError) return <ErrorState error={existing.error} />;

  const busy = saveDraft.isPending || publish.isPending;
  const catalogRefs = new Set(capabilityGroups(toolCatalog.data, { workflows: "", memory: "" }).flatMap((tool) => tool.actions.map((action) => action.ref)));
  const hasInvalidCapabilityRefs = form.available.some((capability) => !catalogRefs.has(capability.ref));
  const error = publish.error ?? saveDraft.error;
  const fieldErrors = validationIssues(error);
  const fieldError = (field: string) => fieldErrors[field] ?? Object.entries(fieldErrors).find(([path]) => path.startsWith(`${field}.`))?.[1];

  return (
    <>
      <PageHeader
        title={isNew ? t("editor.newTitle") : t("editor.editTitle")}
        description={t("editor.description")}
        sacredIcon="quill"
        sacredCaption="holy.editorCaption"
        actions={
          <>
            <Button variant="secondary" disabled={busy} onClick={() => saveDraft.mutate()}>{t("editor.saveDraft")}</Button>
            <Button disabled={busy || !toolCatalog.isSuccess || hasInvalidCapabilityRefs} onClick={() => publish.mutate()}>{t("editor.publish")}</Button>
          </>
        }
      />

      {error && !reportFromError(error) ? <div className="mb-4"><ErrorState error={error} /></div> : null}
      {publish.isPending ? <p role="status" className="text-secondary mb-4 flex items-center gap-2 text-sm"><PopeIcon size={24} />{t("pope.waiting", { pope: t("pope.name") })}</p> : null}
      {publishMessage ? <p role="status" className="text-secondary mb-3 text-sm">{publishMessage}</p> : null}
      {report ? <div className="mb-6"><Card><CardBody><EvalReportView report={report} /></CardBody></Card></div> : null}
      {reportUnavailable ? <div className="mb-6"><Card><CardBody><EvalReportView report={null} /></CardBody></Card></div> : null}

      <form className="flex flex-col gap-6" onSubmit={(e) => e.preventDefault()}>
        <Card>
          <CardHeader><CardTitle sacredIcon="seal">{t("editor.identity")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field label={t("editor.policyId")} htmlFor="policy_id" hint={t("editor.policyIdHint")}>
              <Input id="policy_id" maxLength={200} aria-invalid={Boolean(fieldError("policy_id"))} value={form.policy_id} disabled={!isNew} onChange={(e) => set("policy_id", e.target.value)} />
              <CharacterCounter value={form.policy_id} max={200} />
              <FieldIssue message={fieldError("policy_id")} />
            </Field>
            <Field label={t("editor.domain")} htmlFor="domain">
              <Select id="domain" aria-invalid={Boolean(fieldError("domain"))} value={form.domain} onChange={(e) => set("domain", e.target.value)}>
                <option value="">{t("editor.chooseDomain")}</option>
                {(domains.data ?? []).map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
              </Select>
              <FieldIssue message={fieldError("domain")} />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t("editor.title")} htmlFor="title">
                <Input id="title" maxLength={200} aria-invalid={Boolean(fieldError("title"))} value={form.title} onChange={(e) => set("title", e.target.value)} />
                <CharacterCounter value={form.title} max={200} />
                <FieldIssue message={fieldError("title")} />
              </Field>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="seal">{t("editor.sectionAudience")}</CardTitle></CardHeader>
          <CardBody>
            <AudienceTags
              tags={form.tags}
              disabled={busy}
              issueFor={(key) => fieldErrors[`tags.${key}`]}
              onChange={(tags) => set("tags", tags)}
            />
            <FieldIssue message={fieldErrors.tags} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle sacredIcon="scroll">{t("editor.sources")}</CardTitle>
            <Button variant="ghost" size="sm" disabled={form.sources.length >= 50} onClick={() => set("sources", [...form.sources, { kind: "outro", ref: "" }])}><Plus size={14} aria-hidden /> {t("common.add")}</Button>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            <ItemCounter count={form.sources.length} max={50} />
            <FieldIssue message={fieldError("sources")} />
            {form.sources.length === 0 ? <p className="text-muted text-sm">{t("editor.noSources")}</p> : null}
            <div className="flex flex-col gap-4">
              {form.sources.map((source, i) => (
                <div key={i} className="grid items-start gap-3 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.5fr)_auto]">
                  <Field label={`${t("editor.sources")} ${i + 1}: ${t("editor.sourceType")}`} htmlFor={`source-kind-${i}`}>
                    <Select id={`source-kind-${i}`} aria-invalid={Boolean(fieldError("sources"))} value={source.kind}
                      onChange={(event) => set("sources", form.sources.map((item, j) => (j === i ? { ...item, kind: event.target.value as SourceKind } : item)))}>
                      {SOURCE_KINDS.map((kind) => <option key={kind} value={kind}>{t(`editor.sourceKind.${kind}`)}</option>)}
                    </Select>
                  </Field>
                  <Field label={`${t("editor.sources")} ${i + 1}: ${t("editor.sourceRef")}`} htmlFor={`source-ref-${i}`}>
                    <Input id={`source-ref-${i}`} maxLength={200} aria-invalid={Boolean(fieldError("sources"))} value={source.ref}
                      onChange={(event) => set("sources", form.sources.map((item, j) => (j === i ? { ...item, ref: event.target.value } : item)))} />
                    <CharacterCounter value={source.ref} max={200} />
                  </Field>
                  <Button className="self-end sm:mb-5" variant="ghost" size="sm" aria-label={t("editor.removeSource", { index: i + 1 })} onClick={() => set("sources", form.sources.filter((_, j) => j !== i))}><Trash2 size={14} aria-hidden /></Button>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="tablets">{t("editor.sectionSituationRule")}</CardTitle></CardHeader>
          <CardBody className="flex flex-col gap-4">
            <Field label={t("editor.situation")} htmlFor="situation">
              <Textarea id="situation" maxLength={4000} aria-invalid={Boolean(fieldError("situation"))} value={form.situation} onChange={(e) => set("situation", e.target.value)} />
              <CharacterCounter value={form.situation} max={4000} /><FieldIssue message={fieldError("situation")} />
            </Field>
            <Field label={t("editor.rule")} htmlFor="rule" hint={t("editor.ruleHint")}>
              <Textarea id="rule" maxLength={4000} aria-invalid={Boolean(fieldError("rule"))} value={form.rule} onChange={(e) => set("rule", e.target.value)} />
              <CharacterCounter value={form.rule} max={4000} /><FieldIssue message={fieldError("rule")} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="dove">{t("editor.sectionCustomerText")}</CardTitle></CardHeader>
          <CardBody>
            <Field label={t("editor.customerText")} htmlFor="customer_text">
              <Textarea id="customer_text" maxLength={4000} aria-invalid={Boolean(fieldError("customer_text"))} value={form.customer_text} onChange={(e) => set("customer_text", e.target.value)} />
              <CharacterCounter value={form.customer_text} max={4000} /><FieldIssue message={fieldError("customer_text")} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="keys">{t("editor.sectionAgentSteps")}</CardTitle></CardHeader>
          <CardBody>
            <Field label={t("editor.agentSteps")} htmlFor="agent_steps" hint={t("editor.onePerLine")}>
              <AgentStepsEditor
                value={form.agent_steps}
                catalog={toolCatalog.data}
                disabled={busy}
                invalid={Boolean(fieldError("agent_steps"))}
                onChange={(value) => set("agent_steps", value)}
              />
              <CharacterCounter value={form.agent_steps} max={50} items /><FieldIssue message={fieldError("agent_steps")} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="cross">{t("editor.sectionEscalation")}</CardTitle></CardHeader>
          <CardBody className="flex flex-col gap-4">
            <Field label={t("editor.escalateWhen")} htmlFor="escalate_when" hint={t("editor.escalateWhenHint")}>
              <Textarea id="escalate_when" aria-invalid={Boolean(fieldError("escalate_when"))} value={form.escalate_when} onChange={(e) => set("escalate_when", e.target.value)} />
              <CharacterCounter value={form.escalate_when} max={50} items /><FieldIssue message={fieldError("escalate_when")} />
            </Field>
            <Field label={t("editor.neverSay")} htmlFor="never_say" hint={t("editor.onePerLine")}>
              <Textarea id="never_say" aria-invalid={Boolean(fieldError("never_say"))} value={form.never_say} onChange={(e) => set("never_say", e.target.value)} />
              <CharacterCounter value={form.never_say} max={50} items /><FieldIssue message={fieldError("never_say")} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle sacredIcon="keys">{t("editor.available")}</CardTitle>
            <Button variant="ghost" size="sm" disabled={form.available.length >= 50} onClick={() => set("available", [...form.available, { ref: "", when: "" }])}><Plus size={14} aria-hidden /> {t("common.add")}</Button>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            <p className="text-muted text-sm">{t("editor.availableHint")}</p>
            <CoverageWarning coverage={coverage.data} />
            <ItemCounter count={form.available.length} max={50} />
            <FieldIssue message={fieldError("available")} />
            {toolCatalog.isPending ? <p role="status" className="text-muted text-sm">{t("editor.toolsLoading")}</p> : null}
            {toolCatalog.isError ? <p role="alert" className="text-danger text-sm">{t("editor.toolsUnavailable")}</p> : null}
            {toolCatalog.data?.workflowsAvailable === false ? <p role="note" className="bg-warning-soft text-secondary rounded-md px-3 py-2 text-sm">{t("editor.workflowsUnavailable")}</p> : null}
            {form.available.length === 0 ? <p className="text-muted text-sm">{t("editor.noAvailable")}</p> : null}
            <div className="flex flex-col gap-4">
              {form.available.map((capability, i) => {
                const title = `${t("editor.capability")} ${i + 1}`;
                // One label line on desktop ("Capacidade N: Referência"); on mobile the row header
                // shows "Capacidade N", so the prefix stays only for screen readers.
                const rowLabel = (text: string) => (
                  <span className="block truncate"><span className="sr-only md:not-sr-only">{`${title}: `}</span>{text}</span>
                );
                return (
                  <div
                    key={i}
                    role="group"
                    aria-labelledby={`available-title-${i}`}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_2.5rem]"
                  >
                    <p id={`available-title-${i}`} className="text-foreground col-start-1 row-start-1 self-center text-sm font-medium md:hidden">{title}</p>
                    <div className="col-span-2 row-start-2 min-w-0 md:col-span-1 md:col-start-1 md:row-start-1">
                      <Field label={rowLabel(t("editor.capabilityRef"))} htmlFor={`available-ref-${i}`}>
                        <ToolPicker
                          id={`available-ref-${i}`}
                          label={`${title}: ${t("editor.capabilityRef")}`}
                          value={capability.ref}
                          catalog={toolCatalog.data}
                          disabled={!toolCatalog.isSuccess || busy}
                          onChange={(ref) => set("available", form.available.map((item, j) => (j === i ? { ...item, ref } : item)))}
                        />
                      </Field>
                    </div>
                    <div className="col-span-2 row-start-3 min-w-0 md:col-span-1 md:col-start-2 md:row-start-1">
                      <Field label={rowLabel(t("editor.availableWhen"))} htmlFor={`available-when-${i}`}>
                        <Input id={`available-when-${i}`} maxLength={4000} aria-invalid={Boolean(fieldError("available"))} value={capability.when}
                          onChange={(event) => set("available", form.available.map((item, j) => (j === i ? { ...item, when: event.target.value } : item)))} />
                        <CharacterCounter value={capability.when} max={4000} />
                      </Field>
                    </div>
                    <Button
                      className="col-start-2 row-start-1 justify-self-end md:col-start-3 md:mt-[26px] md:h-10 md:w-10 md:px-0"
                      variant="ghost"
                      size="sm"
                      aria-label={t("editor.removeCapability", { index: i + 1 })}
                      onClick={() => set("available", form.available.filter((_, j) => j !== i))}
                    >
                      <Trash2 size={14} aria-hidden />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="scale">{t("editor.sectionEvals")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field label={t("editor.shouldMatch")} htmlFor="should_match" hint={t("editor.atLeastOneMatch")}><Textarea id="should_match" aria-invalid={Boolean(fieldError("should_match"))} value={form.should_match} onChange={(e) => set("should_match", e.target.value)} /><CharacterCounter value={form.should_match} max={50} items /><FieldIssue message={fieldError("should_match")} /></Field>
            <Field label={t("editor.shouldNotMatch")} htmlFor="should_not_match" hint={t("editor.atLeastOneMatch")}><Textarea id="should_not_match" aria-invalid={Boolean(fieldError("should_not_match"))} value={form.should_not_match} onChange={(e) => set("should_not_match", e.target.value)} /><CharacterCounter value={form.should_not_match} max={50} items /><FieldIssue message={fieldError("should_not_match")} /></Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="scroll">{t("editor.sectionValidity")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field label={t("editor.effectiveFrom")} htmlFor="effective_from" hint={t("editor.effectiveFromDefault")}><Input id="effective_from" type="date" aria-invalid={Boolean(fieldError("effective_from"))} value={form.effective_from} onChange={(e) => set("effective_from", e.target.value)} /><FieldIssue message={fieldError("effective_from")} /></Field>
            <Field label={t("editor.effectiveUntil")} htmlFor="effective_until" hint={t("editor.noEndDate")}><Input id="effective_until" type="date" aria-invalid={Boolean(fieldError("effective_until"))} value={form.effective_until} onChange={(e) => set("effective_until", e.target.value)} /><FieldIssue message={fieldError("effective_until")} /></Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle sacredIcon="scroll">{t("editor.sectionChanges")}</CardTitle></CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field label={t("editor.overrides")} htmlFor="overrides" hint={t("editor.onePerLine")}><Textarea id="overrides" aria-invalid={Boolean(fieldError("overrides"))} value={form.overrides} onChange={(e) => set("overrides", e.target.value)} /><CharacterCounter value={form.overrides} max={50} items /><FieldIssue message={fieldError("overrides")} /></Field>
            <Field label={t("editor.changeReason")} htmlFor="change_reason"><Input id="change_reason" maxLength={200} aria-invalid={Boolean(fieldError("change_reason"))} value={form.change_reason} onChange={(e) => set("change_reason", e.target.value)} /><CharacterCounter value={form.change_reason} max={200} /><FieldIssue message={fieldError("change_reason")} /></Field>
          </CardBody>
        </Card>
      </form>
    </>
  );
}
